import {
  Box,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // Make sure @mui/icons-material is installed
import React, { useState } from "react";
import BackBtn from "../widgets/BackBtn";
import { useParams } from "react-router-dom"; // Removed useNavigate
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import * as XLSX from "xlsx";
import { getHeader } from "../utils/util";
import { API_ENDPOINT } from "../utils/constants";

const CARDS = [
  {
    key: "dtmfCount",
    label: "DTMF Leads",
    sub: "keypress captured",
    icon: "📞",
    accent: "#378ADD",
    iconBg: "#E6F1FB",
    iconColor: "#185FA5",
    apiEndpoint: (id) => `/campaign/get-reports-dtmf/${id}`,
  },
  {
    key: "billingDurationCount",
    label: "Partial Leads",
    sub: "billing ≥ 25 sec",
    icon: "⏱",
    accent: "#1D9E75",
    iconBg: "#E1F5EE",
    iconColor: "#0F6E56",
    apiEndpoint: (id) => `/campaign/get-reports-billing/${id}`,
  },
  {
    key: "whatsappItemsCount",
    label: "WhatsApp Leads",
    sub: "status: read",
    icon: "💬",
    accent: "#639922",
    iconBg: "#EAF3DE",
    iconColor: "#3B6D11",
    apiEndpoint: (id) => `/campaign/get-reports-whatsapp/${id}`,
  },
  {
    key: "rcsItemsCount",
    label: "RCS Leads",
    sub: "status: read",
    icon: "✉️",
    accent: "#D4537E",
    iconBg: "#FBEAF0",
    iconColor: "#993556",
    apiEndpoint: (id) => `/campaign/get-reports-rcs/${id}`,
  },
];

const fmt = (n) => (n || 0).toLocaleString();

const UnifiedReports = () => {
  const { id } = useParams();

  // Modal State
  const [modalState, setModalState] = useState({
    open: false,
    loading: false,
    data: [],
    title: "",
    icon: "",
  });

  const { data, isLoading, isError } = useApiEp(
    `/campaign/get-reports-stats/${id}`
  );

  if (isLoading) return <MuPb />;
  if (isError || !data) return null;

  const total = CARDS.reduce((s, c) => s + (data[c.key] || 0), 0);

  // Handle Card Click (Fetch data & open modal)
  const handleCardClick = async (card) => {
    setModalState({
      open: true,
      loading: true,
      data: [],
      title: card.label,
      icon: card.icon,
    });

    try {
      // NOTE: Replace this fetch block with your authenticated client (e.g., axios) if needed
      const response = await fetch(
        `${API_ENDPOINT}${card.apiEndpoint(id)}`,
        getHeader()
      );
      const result = await response.json();

      // Assume result is an array or result.data is the array
      const items = Array.isArray(result) ? result : result.data || [];

      setModalState((prev) => ({ ...prev, loading: false, data: items }));
    } catch (error) {
      console.error("Failed to fetch lead details:", error);
      setModalState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseModal = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  // Export to XLSX handler
  const handleExport = () => {
    if (!modalState.data || modalState.data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(modalState.data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

    // Clean filename
    const fileName = `${modalState.title.replace(/\s+/g, "_")}_${id}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Generic Table Renderer
  const renderGenericTable = () => {
    if (modalState.loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <MuPb />
        </Box>
      );
    }

    if (!modalState.data || modalState.data.length === 0) {
      return (
        <Typography
          sx={{ py: 3, textAlign: "center", color: "text.secondary" }}
        >
          No records found.
        </Typography>
      );
    }

    // Extract headers dynamically from the first object
    const headers = Object.keys(modalState.data[0]);

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: "60vh" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headers.map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 600,
                    bgcolor: "background.default",
                    textTransform: "capitalize",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* Quick format for snake_case or camelCase to words */}
                  {h.replace(/([A-Z])/g, " $1").replace(/_/g, " ")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {modalState.data.map((row, idx) => (
              <TableRow key={idx} hover>
                {headers.map((h) => (
                  <TableCell key={h} sx={{ whiteSpace: "nowrap" }}>
                    {/* Handle potential nulls/objects safely */}
                    {row[h] !== null && typeof row[h] === "object"
                      ? JSON.stringify(row[h])
                      : String(row[h] ?? "-")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container sx={{ mt: 3, pb: 6 }}>
      <BackBtn />
      <Typography variant="h6">Leads Area</Typography>

      {/* ── Grand total + segmented bar ── */}
      <Box
        sx={{
          mt: 3,
          p: "1.25rem 1.5rem",
          borderRadius: 3,
          border: "0.5px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
            Combined total
          </Box>
          <Box sx={{ fontSize: 32, fontWeight: 500, lineHeight: 1 }}>
            {fmt(total)}
          </Box>
        </Box>

        {/* segmented progress bar */}
        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            height: 10,
            borderRadius: 99,
            overflow: "hidden",
            bgcolor: "action.hover",
            display: "flex",
          }}
        >
          {CARDS.map((c) => (
            <Box
              key={c.key}
              title={`${c.label}: ${fmt(data[c.key])}`}
              sx={{
                height: "100%",
                width: `${total ? ((data[c.key] || 0) / total) * 100 : 0}%`,
                bgcolor: c.accent,
                transition: "width 0.6s ease",
              }}
            />
          ))}
        </Box>

        {/* legend */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {CARDS.map((c) => (
            <Box
              key={c.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  bgcolor: c.accent,
                  flexShrink: 0,
                }}
              />
              {c.label.split(" ")[0]}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Stat cards grid ── */}
      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 2,
        }}
      >
        {CARDS.map((c) => {
          const pct = total
            ? Math.round(((data[c.key] || 0) / total) * 100)
            : 0;
          return (
            <Box
              key={c.key}
              onClick={() => handleCardClick(c)}
              sx={{
                position: "relative",
                p: "1.5rem 1.25rem",
                borderRadius: 3,
                border: "0.5px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                cursor: "pointer",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                transition: "border-color 0.15s, transform 0.12s",
                "&:hover": {
                  borderColor: "text.primary",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: c.accent,
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: c.iconBg,
                    color: c.iconColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                  }}
                >
                  {c.icon}
                </Box>
                {/* Changed the arrow to indicate a modal pop-up rather than navigation */}
                <Box sx={{ fontSize: 20, color: "text.secondary" }}>⤢</Box>
              </Box>

              <Box sx={{ fontSize: 13, color: "text.secondary" }}>
                {c.label}
              </Box>
              <Box sx={{ fontSize: 36, fontWeight: 500, lineHeight: 1 }}>
                {fmt(data[c.key])}
              </Box>
              <Box
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {pct}% of total &nbsp;·&nbsp; {c.sub}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Data Modal ── */}
      <Dialog
        open={modalState.open}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            m: 0,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {modalState.icon} {modalState.title}
          </Box>
          <IconButton
            onClick={handleCloseModal}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {renderGenericTable()}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={modalState.loading || !modalState.data.length}
          >
            Export to Excel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UnifiedReports;
